/**
 * Sessão do usuário.
 *
 * Zustand e não TanStack Query por decisão registrada no `CLAUDE.md`: Query
 * cuida de dado de servidor, Zustand cuida de sessão e UI. A diferença prática
 * é que a sessão não deve ser refetchada ao focar a janela nem invalidada por
 * chave — ela muda por login, logout e expiração, e mais nada.
 *
 * Não há token guardado aqui. A credencial é um cookie httpOnly que o
 * JavaScript não enxerga; `usuario` é só o que o servidor respondeu sobre quem
 * está autenticado.
 */

import { create } from 'zustand'

import { ErroApi, api, registrarPerdaDeSessao, semearCsrf } from '../lib/api'
import type { Usuario } from '../lib/tipos'

type Estado = {
  usuario: Usuario | null
  /** `true` até a primeira resposta do `/auth/me`. Ver `verificada`. */
  carregando: boolean
  /**
   * Distingue "ainda não perguntei ao servidor" de "perguntei, não tem
   * ninguém". Sem isso o roteador manda para o login no primeiro render e o
   * usuário autenticado perde a rota que digitou.
   */
  verificada: boolean
  erroLogin: string | null
  entrandoEm: boolean

  verificar: () => Promise<void>
  entrar: (username: string, password: string) => Promise<boolean>
  sair: () => Promise<void>
}

export const useSessao = create<Estado>((set) => ({
  usuario: null,
  carregando: true,
  verificada: false,
  erroLogin: null,
  entrandoEm: false,

  async verificar() {
    try {
      const usuario = await api.obter<Usuario>('/auth/me')
      set({ usuario, carregando: false, verificada: true })
    } catch (erro) {
      // 401/403 aqui é o caso normal de quem ainda não entrou — não é falha.
      if (erro instanceof ErroApi && erro.naoAutorizado) {
        set({ usuario: null, carregando: false, verificada: true })
        return
      }
      // Rede fora ou 500: também não há sessão, mas o motivo é outro. Deixamos
      // a tela de login aparecer em vez de travar num spinner eterno.
      set({ usuario: null, carregando: false, verificada: true })
    }
  },

  async entrar(username, password) {
    set({ entrandoEm: true, erroLogin: null })
    try {
      await semearCsrf()
      const usuario = await api.criar<Usuario>('/auth/login', { username, password })
      set({ usuario, entrandoEm: false, erroLogin: null, verificada: true })
      return true
    } catch (erro) {
      const mensagem =
        erro instanceof ErroApi
          ? erro.message
          : 'Não foi possível falar com o servidor. Verifique a conexão.'
      set({ usuario: null, entrandoEm: false, erroLogin: mensagem })
      return false
    }
  },

  async sair() {
    try {
      await api.criar<void>('/auth/logout', {})
    } finally {
      // O logout local acontece mesmo se o POST falhar: manter a tela como se
      // o usuário continuasse logado, depois de ele pedir para sair, é pior do
      // que uma sessão órfã no servidor.
      set({ usuario: null, verificada: true })
    }
  },
}))

// Um 401 em qualquer requisição derruba a sessão local — sem isso o painel
// continuaria mostrando dado velho com o servidor já recusando tudo.
registrarPerdaDeSessao(() => {
  if (useSessao.getState().usuario !== null) {
    useSessao.setState({ usuario: null, verificada: true, carregando: false })
  }
})
